// Treino guardado no aparelho, POR DONO (veto AppSec B4, 02/10). O treino em curso, a fila offline e as recusas
// ficam em chaves com academia e usuário (`gs.treinoAtual:<academiaId>:<usuarioId>`, idem `gs.filaTreinos` e
// `gs.filaTreinosFalhas`): no mesmo aparelho, o aluno B nunca lê, retoma nem envia o treino do aluno A. Sem sessão
// (dono `null`) tudo aqui devolve vazio e não grava nada.
//
// Módulo folha (só depende do cliente central): a sessão (`useSair`) e o app nativo (`aoEsquecerSessao`) chamam
// `limparTreinoAoSair` daqui sem ciclo de import. Envio, sincronização e hooks ficam em treino.ts.
import type { PeriodoLimiteTreino } from "@grindset/server/contratos";
import { api } from "../cliente";
import type { MotivoFalha } from "./falhaSessao";
import type { RegistrarSessaoCorpoV2 } from "./tipos";

/** Dono do treino guardado: a sessão logada (academia + usuário). */
export type DonoTreino = { readonly academiaId: string; readonly usuarioId: string };

/** Mesma sessão (academia e usuário)? `null` só é igual a `null`. */
export const mesmoDono = (a: DonoTreino | null, b: DonoTreino | null): boolean =>
  a === b || (!!a && !!b && a.academiaId === b.academiaId && a.usuarioId === b.usuarioId);

/**
 * Dono em nome de quem uma tela aberta com o treino de `aoAbrir` pode gravar e enviar (AppSec C1, 02/10): o próprio,
 * enquanto a sessão logada (`agora`) for a mesma; `null` (nada é gravado nem enviado) se ela mudou de dono com a tela
 * aberta, como quando outra aba do navegador entra como B. O treino de A fica na chave de A como estava.
 */
export const donoParaGravar = (aoAbrir: DonoTreino | null, agora: DonoTreino | null): DonoTreino | null =>
  aoAbrir && mesmoDono(aoAbrir, agora) ? aoAbrir : null;

const BASE_TREINO = "gs.treinoAtual";
const BASE_FILA = "gs.filaTreinos";
const BASE_FALHAS = "gs.filaTreinosFalhas";

const daDono = (base: string, d: DonoTreino) => `${base}:${d.academiaId}:${d.usuarioId}`;
export const chaveTreinoDe = (d: DonoTreino) => daDono(BASE_TREINO, d);
export const chaveFilaDe = (d: DonoTreino) => daDono(BASE_FILA, d);
export const chaveFalhasDe = (d: DonoTreino) => daDono(BASE_FALHAS, d);

/** Chaves sem dono das versões anteriores: só a migração (treino.ts) lê, uma vez, e apaga. */
export const CHAVES_SEM_DONO = { treino: BASE_TREINO, fila: BASE_FILA, falhas: BASE_FALHAS } as const;

/** Além disso o servidor recusa (7 dias): o treino é descartado sem tentar enviar. */
export const VALIDADE_TREINO_MS = 7 * 24 * 3600_000;

// ── Troca de sessão ─────────────────────────────────────────────────────────
//
// Sobe a cada login e a cada saída. Quem envia treino em laço (fila, migração, treino esquecido) confere antes de
// cada envio: depois de uma troca, o próximo envio sairia com a credencial de outra pessoa.
let geracao = 0;
export const geracaoDaSessao = () => geracao;
export function sessaoTrocou(): void {
  geracao++;
}

/** Avisa "treinando agora" (silencioso: nunca bloqueia o treino). */
export const marcarEmAndamento = {
  iniciar: (grupoId: string) =>
    api.post("/api/eu/treinos/em-andamento", { grupoId }, { semRedirecionar: true }).catch(() => undefined),
  parar: () => api.delete("/api/eu/treinos/em-andamento", { semRedirecionar: true }).catch(() => undefined),
};

// ── Treino em curso (retomada) ─────────────────────────────────────────────

/** `concluidaEm`: quando a série foi concluída (anti-trapaça: o servidor confere o intervalo entre séries). */
export type SerieEmCurso = { cargaKg: number | null; repeticoes: number | null; feita: boolean; concluidaEm?: string };
/** `nome`: nome do exercício quando o treino começou (faixa "saiu da ficha"); ausente em treino de versão anterior. */
export type ItemEmCurso = { fichaItemId: string; nome?: string; series: SerieEmCurso[] };
export type TreinoEmCurso = {
  fichaId: string;
  grupoId: string;
  grupoLetra: string;
  iniciadaEm: string;
  atual: number;
  itens: ItemEmCurso[];
  /**
   * Calculado ao começar e guardado para a retomada: período do limite de treinos com pontos que já estava usado
   * ("treino extra": sem a barra do tempo mínimo); null = o treino vale pontos. Ausente em treino começado por uma
   * versão anterior do app.
   */
  treinoExtra?: PeriodoLimiteTreino | null;
  /** Horas sem série nova para o app encerrar sozinho (regra da academia no começo do treino). */
  esquecidoHoras?: number;
  /** Chave do envio automático (treino esquecido): reaproveitada se o app fechar no meio do envio. */
  chaveEncerramento?: string;
  /**
   * A ficha mudou no meio do treino e saíram exercícios com série feita (nome, ou null se desconhecido): a Execução
   * mostra a faixa "O Treino B foi atualizado" (treino-nao-enviado §4, variante B). `avisoFichaMudou`: já foi vista.
   */
  fichaMudou?: { saiu: (string | null)[] };
  avisoFichaMudou?: boolean;
};

export const treinoValido = (t: Pick<TreinoEmCurso, "iniciadaEm">, agora: number = Date.now()) =>
  agora - Date.parse(t.iniciadaEm) < VALIDADE_TREINO_MS;

/**
 * Treino em curso do dono, guardado no aparelho. Um treino esquecido NÃO some aqui: quem o encerra (na hora da última
 * série, e o registra) é o `useTreinoEsquecido`, no layout do aluno.
 */
export function lerTreinoEmCurso(dono: DonoTreino | null): TreinoEmCurso | null {
  if (!dono) return null;
  const chave = chaveTreinoDe(dono);
  try {
    const bruto = localStorage.getItem(chave);
    if (!bruto) return null;
    const t = JSON.parse(bruto) as TreinoEmCurso;
    if (!treinoValido(t)) {
      localStorage.removeItem(chave);
      return null;
    }
    return t;
  } catch {
    return null;
  }
}

export function gravarTreinoEmCurso(dono: DonoTreino | null, t: TreinoEmCurso | null): void {
  if (!dono) return;
  if (t) localStorage.setItem(chaveTreinoDe(dono), JSON.stringify(t));
  else localStorage.removeItem(chaveTreinoDe(dono));
}

// ── Fila offline ──────────────────────────────────────────────────────────
//
// Fila: corpos prontos para o POST (o mesmo formato de sempre). Falhas: por chave de idempotência, o motivo da
// última recusa do servidor (treino GUARDADO, ver falhaSessao.ts). Treino com recusa não some sozinho: o aluno vê o
// aviso no Início e decide entre "Tentar de novo" e "Descartar".

/**
 * Recusa guardada com o treino. `grupoLetra` quando quem guardou sabia (encerramento automático); `tentativas`:
 * quantas vezes o app já tentou de novo (o aviso mostra "Tentamos de novo às 18h40.").
 */
export type FalhaTreinoGuardado = MotivoFalha & { em: string; grupoLetra?: string; tentativas?: number };
export type TreinoGuardado = { corpo: RegistrarSessaoCorpoV2; falha: FalhaTreinoGuardado };

export function lerFilaBruta(dono: DonoTreino | null): RegistrarSessaoCorpoV2[] {
  if (!dono) return [];
  try {
    return JSON.parse(localStorage.getItem(chaveFilaDe(dono)) ?? "[]") as RegistrarSessaoCorpoV2[];
  } catch {
    return [];
  }
}

export function lerFalhas(dono: DonoTreino | null): Record<string, FalhaTreinoGuardado> {
  if (!dono) return {};
  try {
    return JSON.parse(localStorage.getItem(chaveFalhasDe(dono)) ?? "{}") as Record<string, FalhaTreinoGuardado>;
  } catch {
    return {};
  }
}

function guardadosDe(
  fila: RegistrarSessaoCorpoV2[],
  falhas: Record<string, FalhaTreinoGuardado>,
): TreinoGuardado[] {
  return fila.flatMap((corpo) => {
    const falha = falhas[corpo.chaveIdempotencia];
    return falha ? [{ corpo, falha }] : [];
  });
}

/** Retrato da fila de um dono para as telas (`useSyncExternalStore` exige a mesma referência até mudar). */
export type FotoFila = {
  readonly fila: readonly RegistrarSessaoCorpoV2[];
  readonly guardados: readonly TreinoGuardado[];
  readonly pendentes: number;
};
const FOTO_VAZIA: FotoFila = { fila: [], guardados: [], pendentes: 0 };
/** Por dono: o retrato e o texto guardado de onde ele saiu (mudou por fora, ex.: outra aba, o retrato é refeito). */
const fotos = new Map<string, { fila: string | null; falhas: string | null; foto: FotoFila }>();

function lerTexto(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}
const ouvintes = new Set<() => void>();

function montarFoto(fila: RegistrarSessaoCorpoV2[], falhas: Record<string, FalhaTreinoGuardado>): FotoFila {
  const guardados = guardadosDe(fila, falhas);
  return { fila, guardados, pendentes: fila.length - guardados.length };
}

export function fotoDaFila(dono: DonoTreino | null): FotoFila {
  if (!dono) return FOTO_VAZIA;
  const chave = chaveFilaDe(dono);
  const fila = lerTexto(chave);
  const falhas = lerTexto(chaveFalhasDe(dono));
  const anterior = fotos.get(chave);
  if (anterior && anterior.fila === fila && anterior.falhas === falhas) return anterior.foto;
  const foto = montarFoto(lerFilaBruta(dono), lerFalhas(dono));
  fotos.set(chave, { fila, falhas, foto });
  return foto;
}

export const assinarFila = (f: () => void) => {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
};

/** Grava fila e recusas do dono juntas (recusa sem item na fila é limpa) e avisa as telas. */
export function gravarFila(
  dono: DonoTreino | null,
  fila: RegistrarSessaoCorpoV2[],
  falhas: Record<string, FalhaTreinoGuardado> = lerFalhas(dono),
): void {
  if (!dono) return;
  const chaves = new Set(fila.map((c) => c.chaveIdempotencia));
  const validas = Object.fromEntries(Object.entries(falhas).filter(([k]) => chaves.has(k)));
  if (fila.length > 0) localStorage.setItem(chaveFilaDe(dono), JSON.stringify(fila));
  else localStorage.removeItem(chaveFilaDe(dono));
  if (Object.keys(validas).length > 0) localStorage.setItem(chaveFalhasDe(dono), JSON.stringify(validas));
  else localStorage.removeItem(chaveFalhasDe(dono));
  ouvintes.forEach((f) => f());
}

// ── Saída ───────────────────────────────────────────────────────────────────

/** Donos com chave no aparelho (`<base>:<academiaId>:<usuarioId>`). */
function donosCom(base: string): DonoTreino[] {
  const donos: DonoTreino[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const partes = localStorage.key(i)?.split(":");
    if (partes?.length === 3 && partes[0] === base && partes[1] && partes[2]) {
      donos.push({ academiaId: partes[1], usuarioId: partes[2] });
    }
  }
  return donos;
}

const corpoRecente = (c: RegistrarSessaoCorpoV2, agora: number) =>
  agora - Date.parse(c.encerradaEm ?? c.iniciadaEm) < VALIDADE_TREINO_MS;

/**
 * Ao sair (web: `useSair`; app: `aoEsquecerSessao`, também no 401 da sessão morta): desliga o "treinando agora" e
 * apaga das filas os treinos com mais de 7 dias (o servidor já recusaria e o corpo pode ter localização). O treino
 * de cada dono continua guardado para quando ele voltar a entrar.
 */
export function limparTreinoAoSair(agora: number = Date.now()): void {
  sessaoTrocou();
  void marcarEmAndamento.parar();
  try {
    for (const dono of donosCom(BASE_FILA)) {
      const fila = lerFilaBruta(dono);
      const recentes = fila.filter((c) => corpoRecente(c, agora));
      if (recentes.length !== fila.length) gravarFila(dono, recentes);
    }
    for (const dono of donosCom(BASE_TREINO)) lerTreinoEmCurso(dono); // vencido sai na leitura
  } catch {
    // Armazenamento indisponível: nada a limpar.
  }
}
